import { useEffect, useRef, useState } from 'react';
import { Modal, Button, Input, useToast } from '../../components/primitives';
import { useCreateCompany, useUpdateCompany, useDeleteCompany } from '../../api/hooks';
import type { Company } from '../../api/schemas';
import { MapPickerButton } from '../../components/map';
import { DocContactQR } from '../../components/document';
import { AresSearchField, type AresPickResult } from '../../components/ares';
import { parseVCard, type ParsedVCard } from '../../lib/vcard';
import { cx } from '../../lib/cx';
import styles from './NewCompanyModal.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  defaultRole?: 'subdodavatel' | 'investitor' | 'projektant' | 'oba';
  /** Když je předáno, modal edituje existující firmu místo vytvoření nové. */
  company?: Company | null;
  /**
   * Předvyplnění nové firmy z vCard naimportované mimo modal (tlačítko
   * „Import z vCard" v hlavičce stránky). Aplikuje se při otevření.
   */
  initialVCard?: ParsedVCard | null;
}

type RoleOption = { value: string; label: string };
const ROLES: RoleOption[] = [
  { value: 'subdodavatel', label: 'Subdodavatel' },
  { value: 'investitor', label: 'Investor' },
  { value: 'projektant', label: 'Projektant' },
  { value: 'oba', label: 'Kombinace' },
];

/**
 * Přidání / úprava firmy v adresáři — vyhledává v ARES buď podle názvu (našeptávač)
 * nebo podle IČO (lookup tlačítko), předvyplní název, IČO, DIČ a adresu.
 * Při editaci (prop `company`) předvyplní pole, umožní uložit změny nebo firmu smazat.
 */
export function NewCompanyModal({
  open,
  onClose,
  defaultRole = 'subdodavatel',
  company = null,
  initialVCard = null,
}: Props) {
  const isEdit = !!company;
  const [role, setRole] = useState(defaultRole as string);
  const [ico, setIco] = useState('');
  const [nazev, setNazev] = useState('');
  const [adresa, setAdresa] = useState('');
  const [dic, setDic] = useState('');
  const [obor, setObor] = useState('');
  const [kontaktJmeno, setKontaktJmeno] = useState('');
  const [kontaktTel, setKontaktTel] = useState('');
  const [kontaktEmail, setKontaktEmail] = useState('');
  const [poznamka, setPoznamka] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [importNote, setImportNote] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const createMutation = useCreateCompany();
  const updateMutation = useUpdateCompany();
  const deleteMutation = useDeleteCompany();

  // Předvyplnění při otevření (edit) / vyčištění (nová firma)
  useEffect(() => {
    if (!open) return;
    if (company) {
      setRole(company.role || defaultRole);
      setIco(company.ico || '');
      setNazev(company.nazev || '');
      setAdresa(company.adresa || '');
      setDic(company.dic || '');
      setObor(company.obor || '');
      setKontaktJmeno(company.kontakt_jmeno || '');
      setKontaktTel(company.kontakt_tel || '');
      setKontaktEmail(company.kontakt_email || '');
      setPoznamka(company.poznamka || '');
    } else {
      setRole(defaultRole);
      setIco(initialVCard?.ico ?? '');
      setNazev(initialVCard?.nazev ?? '');
      setAdresa(initialVCard?.adresa ?? '');
      setDic(initialVCard?.dic ?? '');
      setObor(initialVCard?.obor ?? '');
      setKontaktJmeno(initialVCard?.kontakt_jmeno ?? '');
      setKontaktTel(initialVCard?.kontakt_tel ?? '');
      setKontaktEmail(initialVCard?.kontakt_email ?? '');
      setPoznamka('');
    }
    setFormError(null);
    setImportNote(initialVCard ? 'Údaje načteny z vCard — zkontroluj a ulož.' : null);
  }, [open, company, defaultRole, initialVCard]);

  function applyAres(data: AresPickResult) {
    setNazev(data.nazev);
    setIco(data.ico);
    setDic(data.dic || '');
    setAdresa(data.adresa);
  }

  function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // umožní znovu vybrat stejný soubor
    if (file) void importFile(file);
  }

  /** Načte vCard ze souboru (výběr i drag&drop) a předvyplní formulář. */
  async function importFile(file: File) {
    try {
      const text = await file.text();
      const parsed = parseVCard(text);
      if (!parsed) {
        setImportNote(null);
        setFormError('Soubor nevypadá jako platná vCard (.vcf).');
        return;
      }
      if (parsed.nazev) setNazev(parsed.nazev);
      if (parsed.ico) setIco(parsed.ico);
      if (parsed.dic) setDic(parsed.dic);
      if (parsed.adresa) setAdresa(parsed.adresa);
      if (parsed.obor) setObor(parsed.obor);
      if (parsed.kontakt_jmeno) setKontaktJmeno(parsed.kontakt_jmeno);
      if (parsed.kontakt_tel) setKontaktTel(parsed.kontakt_tel);
      if (parsed.kontakt_email) setKontaktEmail(parsed.kontakt_email);
      setFormError(null);
      setImportNote('Údaje načteny z vCard — zkontroluj a ulož.');
    } catch {
      setImportNote(null);
      setFormError('Soubor se nepodařilo načíst.');
    }
  }

  async function handleSave() {
    if (!nazev.trim()) {
      setFormError('Název firmy je povinný');
      return;
    }
    setFormError(null);
    const payload = {
      role,
      nazev: nazev.trim(),
      ico: ico.trim(),
      dic: dic.trim(),
      adresa: adresa.trim(),
      kontakt_jmeno: kontaktJmeno.trim(),
      kontakt_tel: kontaktTel.trim(),
      kontakt_email: kontaktEmail.trim(),
      obor: obor.trim(),
      poznamka: poznamka.trim(),
    };
    try {
      if (company) {
        // Zachovej pole, která tento formulář needituje (stavbyvedoucí, BOZP, banka, fakturace…)
        await updateMutation.mutateAsync({
          id: company.id,
          data: {
            stavbyvedouci: company.stavbyvedouci,
            stavbyvedouci_tel: company.stavbyvedouci_tel,
            ckait: company.ckait,
            jednatel: company.jednatel,
            bozp_koordinator: company.bozp_koordinator,
            bozp_tel: company.bozp_tel,
            banka_ucet: company.banka_ucet,
            banka_iban: company.banka_iban,
            fakturacni_adresa: company.fakturacni_adresa,
            fakturacni_email: company.fakturacni_email,
            ...payload,
          },
        });
        toast.success('Firma upravena.');
      } else {
        await createMutation.mutateAsync(payload);
      }
      resetAndClose();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Nepodařilo se uložit firmu');
    }
  }

  async function handleDelete() {
    if (!company) return;
    if (!window.confirm(`Smazat firmu „${company.nazev || 'bez názvu'}" z adresáře? Tuto akci nelze vrátit.`)) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(company.id);
      toast.success('Firma smazána.');
      resetAndClose();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Nepodařilo se smazat firmu');
    }
  }

  function resetAndClose() {
    setFormError(null);
    setImportNote(null);
    onClose();
  }

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      title={isEdit ? 'Upravit firmu' : 'Přidat firmu do adresáře'}
      footer={
        <>
          {isEdit && (
            <Button
              variant="ghost"
              onClick={handleDelete}
              disabled={deleteMutation.isPending || saving}
              style={{ color: 'var(--sd-danger)', marginRight: 'auto' }}
            >
              {deleteMutation.isPending ? 'Mažu…' : '🗑 Smazat'}
            </Button>
          )}
          <Button variant="ghost" onClick={resetAndClose}>
            Zrušit
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Ukládám…' : isEdit ? 'Uložit změny' : 'Uložit firmu'}
          </Button>
        </>
      }
    >
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        {!isEdit && (
          <div
            className={cx(styles.importPanel, dragOver && styles.importPanelDrag)}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const file = e.dataTransfer.files?.[0];
              if (file) void importFile(file);
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".vcf,text/vcard,text/x-vcard"
              hidden
              onChange={handleImportFile}
            />
            <span className={styles.importIcon} aria-hidden>
              📇
            </span>
            <div className={styles.importText}>
              <div className={styles.importTitle}>Máte kontakt v telefonu?</div>
              <div className={styles.importHint}>
                Načtěte firmu z vizitky <strong>.vcf</strong> (vCard) — vyplní název, IČO, adresu
                i kontaktní osobu. Soubor sem můžete i přetáhnout.
              </div>
            </div>
            <Button
              type="button"
              variant="primary"
              onClick={() => fileInputRef.current?.click()}
            >
              ⬆ Načíst vCard
            </Button>
            {importNote && <span className={styles.importNote}>{importNote}</span>}
          </div>
        )}

        <div className={styles.roleRow}>
          {ROLES.map((r) => (
            <label key={r.value} className={styles.rolePill}>
              <input
                type="radio"
                name="role"
                value={r.value}
                checked={role === r.value}
                onChange={() => setRole(r.value)}
              />
              <span>{r.label}</span>
            </label>
          ))}
        </div>

        <AresSearchField
          label="Název firmy"
          placeholder="Stavební firma s.r.o. — pište pro vyhledání v ARES"
          required
          value={nazev}
          onChange={setNazev}
          ico={ico}
          onIcoChange={setIco}
          onPick={applyAres}
          hint="Napište název (našeptávač) nebo zadejte IČO a stiskněte 🔍 ARES"
        />

        <div className={styles.grid2}>
          <Input label="DIČ" mono compact value={dic} onChange={(e) => setDic(e.target.value)} />
          <Input label="Obor / specializace" compact value={obor} onChange={(e) => setObor(e.target.value)} />
        </div>

        <div className={styles.addressRow}>
          <Input label="Adresa" compact value={adresa} onChange={(e) => setAdresa(e.target.value)} />
          <MapPickerButton
            value={adresa}
            onApply={setAdresa}
            title="Adresa firmy"
            label="Mapa"
          />
        </div>

        <div className={styles.sectionTitleRow}>
          <div className={styles.sectionTitle}>Kontakt</div>
          <DocContactQR
            name={kontaktJmeno}
            org={nazev}
            phone={kontaktTel}
            email={kontaktEmail}
            title="QR vizitka — kontakt firmy"
          />
        </div>
        <div className={styles.grid2}>
          <Input
            label="Jméno"
            compact
            value={kontaktJmeno}
            onChange={(e) => setKontaktJmeno(e.target.value)}
          />
          <Input
            label="Telefon"
            compact
            mono
            value={kontaktTel}
            onChange={(e) => setKontaktTel(e.target.value)}
          />
        </div>
        <Input
          label="E-mail"
          compact
          type="email"
          value={kontaktEmail}
          onChange={(e) => setKontaktEmail(e.target.value)}
        />
        <Input
          label="Poznámka"
          compact
          value={poznamka}
          onChange={(e) => setPoznamka(e.target.value)}
        />

        {formError && !formError.includes('IČO') && (
          <div className={styles.error}>{formError}</div>
        )}
      </form>
    </Modal>
  );
}
